<template>
  <v-container>
    <v-dialog v-model="dialogLogOut" max-width="25%">
      <v-card>
        <v-card-title>Confirm close and logout</v-card-title>
        <v-card-text>
          Since you have not verified your email address, you will be signed out in order to verify it.
        </v-card-text>
        <v-card-actions class="mt-10">
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="closeLogOut">Cancel</v-btn>
          <v-btn variant="text" @click="logOutConfirm">Logout</v-btn>
          <v-spacer></v-spacer>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-row align="start" justify="start">
      <v-col cols="12">
        <v-card elevation="0">
          <v-form lazy-validation @submit.prevent="validateAndSaveProfile" ref="profile_form" :disabled="dialogDisabled">
            <v-card-title>
              <v-row no-gutters>
                <v-col>Profile</v-col>
                <v-col class="text-right">
                  <v-icon size="small" color="success" v-tooltip="'Profile complete'" v-if="profileComplete()">
                    mdi-check-circle
                  </v-icon>
                  <v-icon class="error" size="small" v-tooltip="'Profile not complete'" v-else>
                    mdi-alert-circle
                  </v-icon>
                </v-col>
              </v-row>
            </v-card-title>
            <v-card-subtitle class="text-wrap">{{ subTitle }}</v-card-subtitle>
            <v-card-text class="error" v-if="failedSave">{{ failedSave }}</v-card-text>
            <v-card-text>
              <v-container>
                <v-row>
                  <v-col cols="6">
                    <v-text-field label="Username" :readonly="true" :focused="true" variant="plain"
                                  @update:focused="() => {}">
                      {{ username }}
                    </v-text-field>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col cols="6">
                    <v-text-field v-model="newEmail" :rules="rules.email" label="Email">
                      <template v-slot:append>
                        <v-icon class="ms-2" size="small" v-tooltip="'Email address changed'" v-if="(email !== newEmail)">
                          mdi-alert-circle-outline
                        </v-icon>
                        <v-icon class="ms-2" size="small" color="success" v-tooltip="'Email address verified'" v-else-if="emailVerified">
                          mdi-check-circle
                        </v-icon>
                        <v-icon class="ms-2 error" size="small" v-tooltip="'Email address not verified'" v-else>
                          mdi-alert-circle
                        </v-icon>
                      </template>
                    </v-text-field>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col cols="6">
                    <v-text-field v-model="firstName"
                                  :rules="rules.firstName"
                                  class="error-styled"
                                  label="First name"
                                  append-inner-icon="mdi-alert-circle"></v-text-field>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col cols="6">
                    <v-text-field v-model="lastName"
                                  :rules="rules.lastName"
                                  class="error-styled"
                                  label="Last name"
                                  append-inner-icon="mdi-alert-circle"></v-text-field>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col cols="6">
                    <v-text-field v-model="jobTitle"
                                  :rules="rules.jobTitle"
                                  class="error-styled"
                                  label="Job title"
                                  append-inner-icon="mdi-alert-circle"></v-text-field>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col cols="6">
                    <v-text-field v-model="affiliation"
                                  :rules="rules.affiliation"
                                  class="error-styled"
                                  label="Affiliation"
                                  hint="E.g., university or institution."
                                  append-inner-icon="mdi-alert-circle"
                                  persistent-hint>
                    </v-text-field>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col cols="6">
                    <v-phone-input
                        v-model="phone"
                        :rules="rules.phone"
                        class="error-styled"
                        label="Phone"
                        append-inner-icon="mdi-alert-circle"
                        :invalid-message="({ example }) => `Please enter a valid phone number. (Example: ${example})`"
                        country-icon-mode="svg"
                        :enable-searching-country="true"
                        :guess-country="true"
                        :wrapper-props="{ class: 'phone-grid' }"
                        :country-props="{ class: 'country-cell' }"
                        :phone-props="{ class: 'number-cell' }"
                        density="comfortable">
                    </v-phone-input>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col cols="6">
                    <v-select v-model="countryCode"
                              :items="countryCodeItems"
                              :rules="rules.countryCode"
                              class="error-styled"
                              label="Country code"
                              hint="Two-letter country code, e.g., SE for Sweden."
                              append-inner-icon="mdi-alert-circle"
                              persistent-hint>
                    </v-select>
                  </v-col>
                </v-row>
              </v-container>
            </v-card-text>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn type="submit" variant="text" :disabled="dialogDisabled">Save</v-btn>
            </v-card-actions>
          </v-form>
        </v-card>
      </v-col>
    </v-row>
    <v-row v-if="displayCv">
      <v-col cols="12">
        <v-card elevation="0">
            <v-card-title>Curriculum Vitae</v-card-title>
            <v-card-subtitle class="text-wrap">{{ cvSubTitle }}</v-card-subtitle>
            <v-card-text class="error" v-if="failedCvSave">{{ failedCvSave }}</v-card-text>
            <v-card-text>
              <v-container>
                <v-row>
                  <v-col cols="6">
                    <v-file-input v-model="cvFile"
                                  @update:model-value="() => saveCv(cvFile)"
                                  label="Curriculum vitae file"
                                  placeholder="Upload your document"
                                  hint="Whenever you are uploading information, please ensure that you have permission to do so."
                                  prepend-icon="mdi-paperclip"
                                  accept=".doc,application/msword,
                                  .docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document,
                                  .pdf,application/pdf"
                                  variant="outlined"
                                  :multiple="false"
                                  :disabled="dialogDisabled"
                                  persistent-hint>
                      <template v-slot:selection="{ fileNames }">
                        <template v-for="fileName in fileNames" :key="fileName">
                          <v-chip class="me-2" color="primary" size="small" label>
                            {{ fileName }}
                          </v-chip>
                        </template>
                      </template>
                    </v-file-input>
                  </v-col>
                </v-row>
              </v-container>
            </v-card-text>
        </v-card>
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12">
        <ProgressBar mode="indeterminate" style="height: 4px" :hidden="!dialogDisabled"></ProgressBar>
      </v-col>
    </v-row>
    <v-card-actions>
      <v-spacer></v-spacer>
      <v-btn variant="text" @click="close" :disabled="dialogDisabled || (allFieldsRequired && !profileComplete())">Close</v-btn>
    </v-card-actions>
  </v-container>
</template>

<script>
import {defineComponent} from 'vue';
import axios from "axios";
import ProgressBar from 'primevue/progressbar';
import { VPhoneInput } from 'v-phone-input';

const countryCodes = require('country-codes-list')

function isValidEmail(email) {
  const regex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  return regex.test(email);
}

export default defineComponent({
  name: 'ProfileForm',
  props: {
    subTitle: String,
    cvSubTitle: String,
    displayCv: {
      type: Boolean,
      default: false
    },
    allFieldsRequired: {
      type: Boolean,
      default: false
    }
  },
  components: {
    ProgressBar,
    VPhoneInput
  },
  data: context => ({
    dialogDisabled: true,
    dialogLogOut: false,
    dialogLogOutAfterSave: false,
    failedSave: undefined,
    failedCvSave: undefined,
    userId: undefined,
    username: undefined,
    email: undefined,
    newEmail: undefined,
    emailVerified: undefined,
    firstName: undefined,
    lastName: undefined,
    jobTitle: undefined,
    affiliation: undefined,
    phone: undefined,
    countryCode: undefined,
    cvFile: undefined,
    countryCodeItems: [''].concat(
        ['UK', 'EL'].concat(Object.keys(countryCodes.customList('countryCode'))).sort()),
    rules: {
      email: [
        value => {
          if (value && isValidEmail(value)) return true
          return 'You must enter a valid email address.'
        }
      ],
      firstName: [
        value => {
          if (value) return true
          return 'You must enter a first name.'
        }
      ],
      lastName: [
        value => {
          if (value) return true
          return 'You must enter a last name.'
        }
      ],
      jobTitle: [
        value => {
          if (value || !context.allFieldsRequired) return true
          return 'You must enter a job title.'
        }
      ],
      affiliation: [
        value => {
          if (value || !context.allFieldsRequired) return true
          return 'You must enter an affiliation.'
        }
      ],
      phone: [
        value => {
          if (value || !context.allFieldsRequired) return true
          return 'Please enter a valid phone number.'
        }
      ],
      countryCode: [
        value => {
          if ((value?.length === 2) || !context.allFieldsRequired) return true
          return 'You must enter a country code.'
        }
      ],
    }
  }),
  created: function() {
    return this.refreshData().then(() => {
      if (this.displayCv) {
        return this.loadCv()
      }
    }).then(() => {
      this.dialogDisabled = false
    });
  },
  computed: {
    keycloakUrl: function () {
      return new URL("/realms/" + process.env.VUE_APP_KEYCLOAK_REALM + "/account", process.env.VUE_APP_KEYCLOAK_URL)
    }
  },
  methods: {
    forceRefreshToken() {
      return this.$store.dispatch('forceRefreshToken')
    },
    refreshData() {
      return this.forceRefreshToken().then(result => {
        this.userId = result.user_id
        this.username = result.username
        this.email = result.email
        this.newEmail = result.email
        this.emailVerified = result.email_verified
        this.firstName = result.first_name
        this.lastName = result.last_name
        this.jobTitle = result.job_title
        this.affiliation = result.affiliation
          this.phone = result.phone
          this.countryCode = result.country_code
      });
    },
    validateAndSaveProfile() {
      this.dialogDisabled = true
      this.failedSave = undefined
      this.$refs.profile_form.validate().then(validationResult => {
        if (validationResult.valid) {
          return this.save().then(() => {
            return this.refreshData();
          }).catch(err => {
            if (err.response.status === 409) {
              this.failedSave = 'There is already a user with that email address. Please use another one!'
            } else {
              this.failedSave = 'Failed to save profile! Please check that you have entered all fields correctly.'
            }
            console.log(err);
          });
        } else {
          this.failedSave = 'There are validation errors in the form. Please correct them and save before closing.'
        }
      }).finally(() => {
        this.dialogDisabled = false
        if (!this.emailVerified) {
          this.dialogLogOut = true;
        }
      });
    },
    async save() {
      const data = {
        id: this.userId,
        username: this.username,
        firstName: this.firstName,
        lastName: this.lastName,
        email: this.newEmail,
        attributes: {
          ...(this.jobTitle && {jobTitle: this.jobTitle}),
          ...(this.affiliation && {affiliation: this.affiliation}),
          ...(this.phone && {phone: this.phone}),
          ...(this.countryCode && {countryCode: this.countryCode})
        }
      }
      return axios.post(this.keycloakUrl.toString(), data, {
        headers: { Authorization: `Bearer ${this.$store.getters.stateUser.keycloak_token}` }
      }).then(response => {
        this.$emit('saved')
        return response;
      });
    },
    async saveCv(cvFile) {
      this.dialogDisabled = true
      this.failedCvSave = undefined
      const formData = new FormData();
      formData.append("user_id", this.userId);
      if (cvFile) {
        formData.append("cv_file", cvFile);
      }
      return axios.post('/virtual-panel-cvs', formData, {
        headers: { Authorization: `Bearer ${this.$store.getters.stateUser.keycloak_token}` }
      }).then((response) => {
        if (response.data) {
          this.cvFile = new File([], response.data.filename, {
            type: response.data.content_type,
          })
        } else {
          this.cvFile = undefined
        }
      }).catch(err => {
        if (err.response.status === 413) {
          this.failedCvSave = 'Failed to save CV! ' + err.response.data.detail
        } else {
          this.failedCvSave = 'Failed to save CV! You can close and reopen this dialog to make another attempt.';
        }
        console.log(err);
      }).then(() => {
        return this.forceRefreshToken();
      }).finally(() => {
        this.dialogDisabled = false;
      });
    },
    async loadCv() {
      let data = axios.get('/virtual-panel-cvs/' + this.userId, {
        headers: { Authorization: `Bearer ${this.$store.getters.stateUser.keycloak_token}` },
        params: {}
      });
      return data.then((response) => {
        this.cvFile = new File([], response.data.filename, {
          type: response.data.content_type,
        })
      }).catch(err => {
        if (err.response.status === 404) {
          this.cvFile = undefined
        } else {
          throw err;
        }
      });
    },
    close() {
      this.failedSave = undefined
      this.$refs.profile_form.validate().then(validationResult => {
        if (validationResult.valid) {
          if (!this.emailVerified) {
            this.dialogLogOut = true;
            return;
          }
          this.$emit('closed')
        } else {
          this.failedSave = 'There are validation errors in the form. Please correct them and save before closing.'
        }
      })
    },
    async logout() {
      await this.$store.dispatch('logOut');
    },
    closeLogOut() {
      this.dialogLogOut = false;
    },
    logOutConfirm() {
      this.logout().then(() => {
        this.dialogLogOut = false;
      });
    },
    profileComplete() {
      return this.$store.getters.stateUser.email?.trim().length > 0 &&
          this.$store.getters.stateUser.email_verified &&
          this.$store.getters.stateUser.first_name?.trim().length > 0 &&
          this.$store.getters.stateUser.last_name?.trim().length > 0 &&
          this.$store.getters.stateUser.job_title?.trim().length > 0 &&
          this.$store.getters.stateUser.affiliation?.trim().length > 0 &&
          this.$store.getters.stateUser.phone?.trim().length > 0 &&
          this.$store.getters.stateUser.country_code?.trim().length === 2 &&
          this.$store.getters.stateUser.cv_file_available
    },
  }
});
</script>

<style lang="scss" scoped>
.error {
  color: indianred;
}

:deep(.error-styled .v-field--error) {
  --v-field-overlay-opacity: 1;
}

:deep(.error-styled .v-field--error .v-field__overlay) {
  background-color: indianred !important;
}

:deep(.error-styled .v-field--error .v-field__append-inner),
:deep(.error-styled .v-field--error .v-field__input) {
  background: transparent !important;
}

:deep(.error-styled .v-field--error .v-field__append-inner .v-icon) {
  color: indianred !important;
}

:deep(.error-styled .v-field:not(.v-field--error) .v-field__append-inner .v-icon) {
  opacity: 0;
  transition: opacity .12s ease;
}

:deep(.error-styled .v-field) { overflow: hidden; }

:deep(.phone-grid){
  display: grid;
  grid-template-columns: 160px 1fr;
  column-gap: 8px;
  align-items: end;
}

@media (max-width: 960px) {
  :deep(.phone-grid){
    grid-template-columns: 1fr;
    row-gap: 8px;
    align-items: stretch;
  }
}

:deep(.country-cell){ grid-area: country; }
:deep(.number-cell){  grid-area: number;  }
@media (max-width: 600px){
  :deep(.phone-grid){
    grid-template-areas:
      "country"
      "number";
  }
}

@layer primevue {
  .p-progressbar {
    background-color: #364c5b;
  }
}
</style>

